import { useRef, useState } from "react";
import clinicAPI from "../services/clinicApi";
import {
  DEFAULT_REACTION,
  applyOptimisticReaction,
} from "../constants/reactions";

export function useClubLifeInteractions(setPosts, { commentsApi = "client" } = {}) {
  const isStaff = commentsApi === "admin";
  const [commentBusy, setCommentBusy] = useState(false);
  const [openComments, setOpenComments] = useState(null);
  const [commentsByPost, setCommentsByPost] = useState({});
  const likingRef = useRef(new Set());

  const toggleLike = async (post, emoji = DEFAULT_REACTION) => {
    if (!post?.id || likingRef.current.has(post.id)) return;

    const snapshot = {
      reactions: { ...(post.reactions || {}) },
      my_reaction: post.my_reaction || null,
      likes_count: Number(post.likes_count) || 0,
      liked_by_me: Boolean(post.liked_by_me),
    };
    const optimistic = applyOptimisticReaction(post, emoji);

    likingRef.current.add(post.id);
    setPosts((prev) =>
      prev.map((p) => (p.id === post.id ? { ...p, ...optimistic } : p)),
    );

    try {
      const toggle = isStaff
        ? clinicAPI.toggleAdminClubLifeLike
        : clinicAPI.toggleClubLifeLike;
      const r = await toggle(post.id, emoji);
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? {
                ...p,
                liked_by_me: Boolean(r.liked ?? r.my_reaction),
                likes_count: r.likes_count ?? p.likes_count,
                my_reaction: r.my_reaction ?? null,
                reactions: r.reactions || {},
              }
            : p,
        ),
      );
    } catch {
      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? { ...p, ...snapshot } : p)),
      );
    } finally {
      likingRef.current.delete(post.id);
    }
  };

  const loadComments = async (postId) => {
    if (openComments === postId) {
      setOpenComments(null);
      return;
    }
    setOpenComments(postId);
    if (commentsByPost[postId]) return;
    try {
      const fetchComments = isStaff
        ? clinicAPI.getAdminClubLifePostComments
        : clinicAPI.getClubLifeComments;
      const data = await fetchComments(postId);
      setCommentsByPost((prev) => ({ ...prev, [postId]: data.comments || [] }));
    } catch {
      setCommentsByPost((prev) => ({ ...prev, [postId]: [] }));
    }
  };

  const addComment = async (postId, body, clear, parentId = null) => {
    setCommentBusy(true);
    try {
      const send = isStaff
        ? clinicAPI.addAdminClubLifeComment
        : clinicAPI.addClubLifeComment;
      const r = await send(postId, {
        body,
        ...(parentId ? { parent_id: parentId } : {}),
      });
      setCommentsByPost((prev) => ({
        ...prev,
        [postId]: [...(prev[postId] || []), r.comment],
      }));
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, comments_count: r.comments_count } : p,
        ),
      );
      clear();
    } finally {
      setCommentBusy(false);
    }
  };

  return {
    commentBusy,
    openComments,
    commentsByPost,
    toggleLike,
    loadComments,
    addComment,
  };
}
